import { z } from 'zod';

import { fixture } from './fixtures';
import { Condition, GroupType } from './groups';
import { User } from './users';

export enum AwardVisibility {
	Public = 'publicly_visible',
	Private = 'privately_visible',
	Team = 'immediate_team',
}

export enum AwardState {
	Active = 'active',
	Inactive = 'inactive',
}

export enum BudgetPeriod {
	Week = 'week',
	Month = 'month',
	Quarter = 'quarter',
	Year = 'year',
	Lifetime = 'lifetime',
}

export const Award = z.object({
	id: z.string(),
	amount: z.number().nullable().default(0),
	amountEditable: z.boolean(),
	budgetUsed: z.number().nullable(),
	conditionsOperator: z.enum(GroupType).nullable(),
	conditions: z.array(Condition).nullable(),
	giverId: z.string(),
	hideGiver: z.boolean().catch(false),
	iconUrl: z.string().nullable().default(''),
	message: z.string().nullable().default(''),
	name: z.string(),
	state: z.enum(AwardState),
	periodType: z.enum(BudgetPeriod).nullable(),
	visibility: z.enum(AwardVisibility),
	budgetPerPeriod: z.number().nullable().default(0),
	messageEditable: z.boolean(),
	pendingApprovals: z.number(),
});

export type Award = z.infer<typeof Award>;

export const AwardDetail = Award.extend({
	description: z.string().nullable().default(''),
	visibilityEditable: z.boolean(),
	title: z.string(),
	approvalLevels: z.array(z.array(User)),
});

export type AwardDetail = z.infer<typeof AwardDetail>;

// Fixture functions using manual generators
export const awardFixture = (overrides: Partial<Award> = {}): Award => ({
	id: fixture.id(),
	amount: fixture.int(10, 100),
	amountEditable: fixture.boolean(false),
	budgetUsed: null,
	conditionsOperator: null,
	conditions: null,
	giverId: fixture.id(),
	hideGiver: fixture.boolean(false),
	iconUrl: fixture.url('icon'),
	message: fixture.description('award'),
	name: fixture.name('Award'),
	state: AwardState.Active,
	periodType: null,
	visibility: AwardVisibility.Public,
	budgetPerPeriod: fixture.int(100, 1000),
	messageEditable: fixture.boolean(false),
	pendingApprovals: 0,
	...overrides,
});

export const awardDetailFixture = (overrides: Partial<AwardDetail> = {}): AwardDetail => ({
	...awardFixture(),
	description: fixture.description('award'),
	visibilityEditable: fixture.boolean(true),
	title: fixture.name('Award Title'),
	approvalLevels: [],
	...overrides,
});

// Utility functions (replicating original getter methods)
export const isAwardActive = (award: { state: AwardState }): boolean => award.state === AwardState.Active;
